import type { Direction, SpriteData } from "../types/index.js";
import { Direction as Dir } from "../types/index.js";
import { getLoadedCharacters, type LoadedCharacterData } from "./loader.js";

const _ = ""; // transparent

// Character color palettes (13+ distinct styles for band members + user avatar)
export const CHARACTER_PALETTES = [
  // 0: Orchestrator - golden/executive (also user avatar style)
  { skin: "#FFCC99", shirt: "#FFD000", pants: "#1A1A2E", hair: "#443322", shoes: "#111111" },
  // 1: Researcher - blue academic
  { skin: "#FFCC99", shirt: "#4488CC", pants: "#334466", hair: "#553322", shoes: "#222222" },
  // 2: Analyst - purple data
  { skin: "#DEB887", shirt: "#9B59B6", pants: "#2C3E50", hair: "#222222", shoes: "#333333" },
  // 3: BI - deep purple
  { skin: "#FFCC99", shirt: "#8E44AD", pants: "#1A1A2E", hair: "#AA4422", shoes: "#222222" },
  // 4: Coder - green dev
  { skin: "#FFCC99", shirt: "#50C878", pants: "#333333", hair: "#111111", shoes: "#222222" },
  // 5: Security - dark tactical
  { skin: "#DEB887", shirt: "#2C3E50", pants: "#1A1A2E", hair: "#222222", shoes: "#111111" },
  // 6: Communicator - red creative
  { skin: "#FFCC99", shirt: "#E74C3C", pants: "#443322", hair: "#FFD700", shoes: "#222222" },
  // 7: Support - teal friendly
  { skin: "#DEB887", shirt: "#1ABC9C", pants: "#334444", hair: "#553322", shoes: "#333333" },
  // 8: Sales - green business
  { skin: "#FFCC99", shirt: "#27AE60", pants: "#334466", hair: "#443322", shoes: "#222222" },
  // 9: Finance - orange/gold
  { skin: "#FFCC99", shirt: "#F39C12", pants: "#2C3E50", hair: "#111111", shoes: "#222222" },
  // 10: Legal - charcoal formal
  { skin: "#DEB887", shirt: "#34495E", pants: "#1A1A2E", hair: "#222222", shoes: "#111111" },
  // 11: People - pink HR
  { skin: "#FFCC99", shirt: "#E91E63", pants: "#443355", hair: "#AA4422", shoes: "#222222" },
  // 12: PM - blue project
  { skin: "#DEB887", shirt: "#3498DB", pants: "#334466", hair: "#553322", shoes: "#333333" },
  // 13: User Avatar (CEO) - same as orchestrator
  { skin: "#FFCC99", shirt: "#FFD000", pants: "#1A1A2E", hair: "#443322", shoes: "#111111" },
] as const;

interface CharPalette {
  skin: string;
  shirt: string;
  pants: string;
  hair: string;
  shoes: string;
}

// Template keys
const H = "hair";
const K = "skin";
const S = "shirt";
const P = "pants";
const O = "shoes";
const E = "#FFFFFF"; // eyes

type TemplateCell = typeof H | typeof K | typeof S | typeof P | typeof O | typeof E | typeof _;

function resolveTemplate(template: TemplateCell[][], palette: CharPalette): SpriteData {
  return template.map((row) =>
    row.map((cell) => {
      if (cell === _) return "";
      if (cell === E) return E;
      if (cell === H) return palette.hair;
      if (cell === K) return palette.skin;
      if (cell === S) return palette.shirt;
      if (cell === P) return palette.pants;
      if (cell === O) return palette.shoes;
      return cell;
    }),
  );
}

function flipHorizontal(template: TemplateCell[][]): TemplateCell[][] {
  return template.map((row) => [...row].reverse());
}

// Down-facing walk frames
const CHAR_WALK_DOWN_1: TemplateCell[][] = [
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, H, H, H, H, _, _, _, _, _, _],
  [_, _, _, _, _, H, H, H, H, H, H, _, _, _, _, _],
  [_, _, _, _, _, H, H, H, H, H, H, _, _, _, _, _],
  [_, _, _, _, _, K, K, K, K, K, K, _, _, _, _, _],
  [_, _, _, _, _, K, E, K, K, E, K, _, _, _, _, _],
  [_, _, _, _, _, K, K, K, K, K, K, _, _, _, _, _],
  [_, _, _, _, _, K, K, K, K, K, K, _, _, _, _, _],
  [_, _, _, _, _, _, S, S, S, S, _, _, _, _, _, _],
  [_, _, _, _, _, S, S, S, S, S, S, _, _, _, _, _],
  [_, _, _, _, S, S, S, S, S, S, S, S, _, _, _, _],
  [_, _, _, _, S, S, S, S, S, S, S, S, _, _, _, _],
  [_, _, _, _, K, S, S, S, S, S, S, K, _, _, _, _],
  [_, _, _, _, _, S, S, S, S, S, S, _, _, _, _, _],
  [_, _, _, _, _, _, P, P, P, P, _, _, _, _, _, _],
  [_, _, _, _, _, P, P, P, P, P, P, _, _, _, _, _],
  [_, _, _, _, _, P, P, P, P, P, P, _, _, _, _, _],
  [_, _, _, _, P, P, _, _, _, _, P, P, _, _, _, _],
  [_, _, _, _, P, P, _, _, _, _, P, P, _, _, _, _],
  [_, _, _, _, O, O, _, _, _, _, _, O, O, _, _, _],
  [_, _, _, _, O, O, _, _, _, _, _, O, O, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
];

const CHAR_WALK_DOWN_2: TemplateCell[][] = [
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, H, H, H, H, _, _, _, _, _, _],
  [_, _, _, _, _, H, H, H, H, H, H, _, _, _, _, _],
  [_, _, _, _, _, H, H, H, H, H, H, _, _, _, _, _],
  [_, _, _, _, _, K, K, K, K, K, K, _, _, _, _, _],
  [_, _, _, _, _, K, E, K, K, E, K, _, _, _, _, _],
  [_, _, _, _, _, K, K, K, K, K, K, _, _, _, _, _],
  [_, _, _, _, _, K, K, K, K, K, K, _, _, _, _, _],
  [_, _, _, _, _, _, S, S, S, S, _, _, _, _, _, _],
  [_, _, _, _, _, S, S, S, S, S, S, _, _, _, _, _],
  [_, _, _, _, S, S, S, S, S, S, S, S, _, _, _, _],
  [_, _, _, _, S, S, S, S, S, S, S, S, _, _, _, _],
  [_, _, _, _, K, S, S, S, S, S, S, K, _, _, _, _],
  [_, _, _, _, _, S, S, S, S, S, S, _, _, _, _, _],
  [_, _, _, _, _, _, P, P, P, P, _, _, _, _, _, _],
  [_, _, _, _, _, P, P, P, P, P, P, _, _, _, _, _],
  [_, _, _, _, _, P, P, _, _, P, P, _, _, _, _, _],
  [_, _, _, _, _, P, P, _, _, P, P, _, _, _, _, _],
  [_, _, _, _, _, P, P, _, _, P, P, _, _, _, _, _],
  [_, _, _, _, _, O, O, _, _, O, O, _, _, _, _, _],
  [_, _, _, _, _, O, O, _, _, O, O, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
];

// Down typing (seated at desk)
const CHAR_DOWN_TYPE_1: TemplateCell[][] = [
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, H, H, H, H, _, _, _, _, _, _],
  [_, _, _, _, _, H, H, H, H, H, H, _, _, _, _, _],
  [_, _, _, _, _, H, H, H, H, H, H, _, _, _, _, _],
  [_, _, _, _, _, K, K, K, K, K, K, _, _, _, _, _],
  [_, _, _, _, _, K, E, K, K, E, K, _, _, _, _, _],
  [_, _, _, _, _, K, K, K, K, K, K, _, _, _, _, _],
  [_, _, _, _, _, K, K, K, K, K, K, _, _, _, _, _],
  [_, _, _, _, _, _, S, S, S, S, _, _, _, _, _, _],
  [_, _, _, _, _, S, S, S, S, S, S, _, _, _, _, _],
  [_, _, _, _, S, S, S, S, S, S, S, S, _, _, _, _],
  [_, _, _, K, K, S, S, S, S, S, S, K, K, _, _, _],
  [_, _, _, _, K, S, S, S, S, S, S, K, _, _, _, _],
  [_, _, _, _, _, S, S, S, S, S, S, _, _, _, _, _],
  [_, _, _, _, _, _, P, P, P, P, _, _, _, _, _, _],
  [_, _, _, _, _, P, P, P, P, P, P, _, _, _, _, _],
  [_, _, _, _, _, P, P, P, P, P, P, _, _, _, _, _],
  [_, _, _, _, _, P, P, _, _, P, P, _, _, _, _, _],
  [_, _, _, _, _, O, O, _, _, O, O, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
];

const CHAR_DOWN_TYPE_2: TemplateCell[][] = [
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, H, H, H, H, _, _, _, _, _, _],
  [_, _, _, _, _, H, H, H, H, H, H, _, _, _, _, _],
  [_, _, _, _, _, H, H, H, H, H, H, _, _, _, _, _],
  [_, _, _, _, _, K, K, K, K, K, K, _, _, _, _, _],
  [_, _, _, _, _, K, E, K, K, E, K, _, _, _, _, _],
  [_, _, _, _, _, K, K, K, K, K, K, _, _, _, _, _],
  [_, _, _, _, _, K, K, K, K, K, K, _, _, _, _, _],
  [_, _, _, _, _, _, S, S, S, S, _, _, _, _, _, _],
  [_, _, _, _, _, S, S, S, S, S, S, _, _, _, _, _],
  [_, _, _, _, S, S, S, S, S, S, S, S, _, _, _, _],
  [_, _, _, _, K, S, S, S, S, S, S, K, K, _, _, _],
  [_, _, _, _, K, S, S, S, S, S, S, _, K, _, _, _],
  [_, _, _, _, _, S, S, S, S, S, S, _, _, _, _, _],
  [_, _, _, _, _, _, P, P, P, P, _, _, _, _, _, _],
  [_, _, _, _, _, P, P, P, P, P, P, _, _, _, _, _],
  [_, _, _, _, _, P, P, P, P, P, P, _, _, _, _, _],
  [_, _, _, _, _, P, P, _, _, P, P, _, _, _, _, _],
  [_, _, _, _, _, O, O, _, _, O, O, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
];

// Right walk
const CHAR_WALK_RIGHT_1: TemplateCell[][] = [
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, H, H, H, H, _, _, _, _, _],
  [_, _, _, _, _, _, H, H, H, H, H, _, _, _, _, _],
  [_, _, _, _, _, _, H, H, H, H, H, _, _, _, _, _],
  [_, _, _, _, _, _, K, K, K, K, K, _, _, _, _, _],
  [_, _, _, _, _, _, K, K, K, E, K, _, _, _, _, _],
  [_, _, _, _, _, _, K, K, K, K, K, _, _, _, _, _],
  [_, _, _, _, _, _, K, K, K, K, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, S, S, S, _, _, _, _, _, _],
  [_, _, _, _, _, _, S, S, S, S, S, _, _, _, _, _],
  [_, _, _, _, _, S, S, S, S, S, S, _, _, _, _, _],
  [_, _, _, _, _, S, S, S, S, S, S, _, _, _, _, _],
  [_, _, _, _, _, K, S, S, S, S, K, _, _, _, _, _],
  [_, _, _, _, _, _, S, S, S, S, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, P, P, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, P, P, P, P, _, _, _, _, _, _],
  [_, _, _, _, _, _, P, P, P, P, _, _, _, _, _, _],
  [_, _, _, _, _, P, P, _, _, _, P, P, _, _, _, _],
  [_, _, _, _, _, P, P, _, _, _, P, P, _, _, _, _],
  [_, _, _, _, _, O, O, _, _, _, _, O, O, _, _, _],
  [_, _, _, _, _, O, O, _, _, _, _, O, O, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
];

const CHAR_WALK_RIGHT_2: TemplateCell[][] = [
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, H, H, H, H, _, _, _, _, _],
  [_, _, _, _, _, _, H, H, H, H, H, _, _, _, _, _],
  [_, _, _, _, _, _, H, H, H, H, H, _, _, _, _, _],
  [_, _, _, _, _, _, K, K, K, K, K, _, _, _, _, _],
  [_, _, _, _, _, _, K, K, K, E, K, _, _, _, _, _],
  [_, _, _, _, _, _, K, K, K, K, K, _, _, _, _, _],
  [_, _, _, _, _, _, K, K, K, K, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, S, S, S, _, _, _, _, _, _],
  [_, _, _, _, _, _, S, S, S, S, S, _, _, _, _, _],
  [_, _, _, _, _, S, S, S, S, S, S, _, _, _, _, _],
  [_, _, _, _, _, S, S, S, S, S, S, _, _, _, _, _],
  [_, _, _, _, _, K, S, S, S, S, K, _, _, _, _, _],
  [_, _, _, _, _, _, S, S, S, S, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, P, P, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, P, P, P, P, _, _, _, _, _, _],
  [_, _, _, _, _, _, P, P, _, P, P, _, _, _, _, _],
  [_, _, _, _, _, _, P, P, _, P, P, _, _, _, _, _],
  [_, _, _, _, _, _, P, P, _, P, P, _, _, _, _, _],
  [_, _, _, _, _, _, O, O, _, O, O, _, _, _, _, _],
  [_, _, _, _, _, _, O, O, _, O, O, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
];

// Up walk (back view)
const CHAR_WALK_UP_1: TemplateCell[][] = [
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, H, H, H, H, _, _, _, _, _, _],
  [_, _, _, _, _, H, H, H, H, H, H, _, _, _, _, _],
  [_, _, _, _, _, H, H, H, H, H, H, _, _, _, _, _],
  [_, _, _, _, _, H, H, H, H, H, H, _, _, _, _, _],
  [_, _, _, _, _, K, K, K, K, K, K, _, _, _, _, _],
  [_, _, _, _, _, K, K, K, K, K, K, _, _, _, _, _],
  [_, _, _, _, _, K, K, K, K, K, K, _, _, _, _, _],
  [_, _, _, _, _, _, S, S, S, S, _, _, _, _, _, _],
  [_, _, _, _, _, S, S, S, S, S, S, _, _, _, _, _],
  [_, _, _, _, S, S, S, S, S, S, S, S, _, _, _, _],
  [_, _, _, _, S, S, S, S, S, S, S, S, _, _, _, _],
  [_, _, _, _, K, S, S, S, S, S, S, K, _, _, _, _],
  [_, _, _, _, _, S, S, S, S, S, S, _, _, _, _, _],
  [_, _, _, _, _, _, P, P, P, P, _, _, _, _, _, _],
  [_, _, _, _, _, P, P, P, P, P, P, _, _, _, _, _],
  [_, _, _, _, _, P, P, P, P, P, P, _, _, _, _, _],
  [_, _, _, _, P, P, _, _, _, _, P, P, _, _, _, _],
  [_, _, _, _, P, P, _, _, _, _, P, P, _, _, _, _],
  [_, _, _, O, O, _, _, _, _, _, _, O, O, _, _, _],
  [_, _, _, O, O, _, _, _, _, _, _, O, O, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
];

const CHAR_WALK_UP_2: TemplateCell[][] = [
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, H, H, H, H, _, _, _, _, _, _],
  [_, _, _, _, _, H, H, H, H, H, H, _, _, _, _, _],
  [_, _, _, _, _, H, H, H, H, H, H, _, _, _, _, _],
  [_, _, _, _, _, H, H, H, H, H, H, _, _, _, _, _],
  [_, _, _, _, _, K, K, K, K, K, K, _, _, _, _, _],
  [_, _, _, _, _, K, K, K, K, K, K, _, _, _, _, _],
  [_, _, _, _, _, K, K, K, K, K, K, _, _, _, _, _],
  [_, _, _, _, _, _, S, S, S, S, _, _, _, _, _, _],
  [_, _, _, _, _, S, S, S, S, S, S, _, _, _, _, _],
  [_, _, _, _, S, S, S, S, S, S, S, S, _, _, _, _],
  [_, _, _, _, S, S, S, S, S, S, S, S, _, _, _, _],
  [_, _, _, _, K, S, S, S, S, S, S, K, _, _, _, _],
  [_, _, _, _, _, S, S, S, S, S, S, _, _, _, _, _],
  [_, _, _, _, _, _, P, P, P, P, _, _, _, _, _, _],
  [_, _, _, _, _, P, P, P, P, P, P, _, _, _, _, _],
  [_, _, _, _, _, P, P, _, _, P, P, _, _, _, _, _],
  [_, _, _, _, _, P, P, _, _, P, P, _, _, _, _, _],
  [_, _, _, _, _, P, P, _, _, P, P, _, _, _, _, _],
  [_, _, _, _, _, O, O, _, _, O, O, _, _, _, _, _],
  [_, _, _, _, _, O, O, _, _, O, O, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
];

// Up typing
const CHAR_UP_TYPE_1: TemplateCell[][] = [
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, H, H, H, H, _, _, _, _, _, _],
  [_, _, _, _, _, H, H, H, H, H, H, _, _, _, _, _],
  [_, _, _, _, _, H, H, H, H, H, H, _, _, _, _, _],
  [_, _, _, _, _, H, H, H, H, H, H, _, _, _, _, _],
  [_, _, _, _, _, K, K, K, K, K, K, _, _, _, _, _],
  [_, _, _, _, _, K, K, K, K, K, K, _, _, _, _, _],
  [_, _, _, _, _, _, S, S, S, S, _, _, _, _, _, _],
  [_, _, _, _, _, S, S, S, S, S, S, _, _, _, _, _],
  [_, _, _, _, S, S, S, S, S, S, S, S, _, _, _, _],
  [_, _, _, K, K, S, S, S, S, S, S, K, K, _, _, _],
  [_, _, _, _, K, S, S, S, S, S, S, K, _, _, _, _],
  [_, _, _, _, _, S, S, S, S, S, S, _, _, _, _, _],
  [_, _, _, _, _, _, P, P, P, P, _, _, _, _, _, _],
  [_, _, _, _, _, P, P, P, P, P, P, _, _, _, _, _],
  [_, _, _, _, _, P, P, P, P, P, P, _, _, _, _, _],
  [_, _, _, _, _, P, P, _, _, P, P, _, _, _, _, _],
  [_, _, _, _, _, O, O, _, _, O, O, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
];

const CHAR_UP_TYPE_2: TemplateCell[][] = [
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, H, H, H, H, _, _, _, _, _, _],
  [_, _, _, _, _, H, H, H, H, H, H, _, _, _, _, _],
  [_, _, _, _, _, H, H, H, H, H, H, _, _, _, _, _],
  [_, _, _, _, _, H, H, H, H, H, H, _, _, _, _, _],
  [_, _, _, _, _, K, K, K, K, K, K, _, _, _, _, _],
  [_, _, _, _, _, K, K, K, K, K, K, _, _, _, _, _],
  [_, _, _, _, _, _, S, S, S, S, _, _, _, _, _, _],
  [_, _, _, _, _, S, S, S, S, S, S, _, _, _, _, _],
  [_, _, _, _, S, S, S, S, S, S, S, S, _, _, _, _],
  [_, _, _, _, K, S, S, S, S, S, S, K, K, _, _, _],
  [_, _, _, _, K, S, S, S, S, S, S, _, K, _, _, _],
  [_, _, _, _, _, S, S, S, S, S, S, _, _, _, _, _],
  [_, _, _, _, _, _, P, P, P, P, _, _, _, _, _, _],
  [_, _, _, _, _, P, P, P, P, P, P, _, _, _, _, _],
  [_, _, _, _, _, P, P, P, P, P, P, _, _, _, _, _],
  [_, _, _, _, _, P, P, _, _, P, P, _, _, _, _, _],
  [_, _, _, _, _, O, O, _, _, O, O, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
];

// Right typing
const CHAR_RIGHT_TYPE_1: TemplateCell[][] = [
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, H, H, H, H, _, _, _, _, _],
  [_, _, _, _, _, _, H, H, H, H, H, _, _, _, _, _],
  [_, _, _, _, _, _, H, H, H, H, H, _, _, _, _, _],
  [_, _, _, _, _, _, K, K, K, K, K, _, _, _, _, _],
  [_, _, _, _, _, _, K, K, K, E, K, _, _, _, _, _],
  [_, _, _, _, _, _, K, K, K, K, K, _, _, _, _, _],
  [_, _, _, _, _, _, K, K, K, K, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, S, S, S, _, _, _, _, _, _],
  [_, _, _, _, _, _, S, S, S, S, S, _, _, _, _, _],
  [_, _, _, _, _, S, S, S, S, S, S, K, _, _, _, _],
  [_, _, _, _, _, S, S, S, S, S, K, _, _, _, _, _],
  [_, _, _, _, _, _, S, S, S, S, _, _, _, _, _, _],
  [_, _, _, _, _, _, S, S, S, S, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, P, P, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, P, P, P, P, _, _, _, _, _, _],
  [_, _, _, _, _, _, P, P, P, P, _, _, _, _, _, _],
  [_, _, _, _, _, _, P, P, _, P, P, _, _, _, _, _],
  [_, _, _, _, _, _, O, O, _, O, O, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
];

const CHAR_RIGHT_TYPE_2: TemplateCell[][] = [
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, H, H, H, H, _, _, _, _, _],
  [_, _, _, _, _, _, H, H, H, H, H, _, _, _, _, _],
  [_, _, _, _, _, _, H, H, H, H, H, _, _, _, _, _],
  [_, _, _, _, _, _, K, K, K, K, K, _, _, _, _, _],
  [_, _, _, _, _, _, K, K, K, E, K, _, _, _, _, _],
  [_, _, _, _, _, _, K, K, K, K, K, _, _, _, _, _],
  [_, _, _, _, _, _, K, K, K, K, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, S, S, S, _, _, _, _, _, _],
  [_, _, _, _, _, _, S, S, S, S, S, _, _, _, _, _],
  [_, _, _, _, _, S, S, S, S, S, S, _, K, _, _, _],
  [_, _, _, _, _, S, S, S, S, S, _, _, K, _, _, _],
  [_, _, _, _, _, _, S, S, S, S, _, _, _, _, _, _],
  [_, _, _, _, _, _, S, S, S, S, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, P, P, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, P, P, P, P, _, _, _, _, _, _],
  [_, _, _, _, _, _, P, P, P, P, _, _, _, _, _, _],
  [_, _, _, _, _, _, P, P, _, P, P, _, _, _, _, _],
  [_, _, _, _, _, _, O, O, _, O, O, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
];

// Character sprites cache
export interface CharacterSprites {
  walk: Record<Direction, [SpriteData, SpriteData, SpriteData, SpriteData]>;
  typing: Record<Direction, [SpriteData, SpriteData]>;
  reading: Record<Direction, [SpriteData, SpriteData]>;
}

const spriteCache = new Map<number, CharacterSprites>();

/**
 * Clear the character sprite cache (call after loading PNG sprites)
 */
export function clearCharacterSpriteCache(): void {
  spriteCache.clear();
}

function flipSpriteHorizontal(sprite: SpriteData): SpriteData {
  return sprite.map((row) => [...row].reverse());
}

/**
 * Build CharacterSprites from loaded PNG data
 * PNG frame order: walk1, walk2, walk3, type1, type2, read1, read2
 */
function buildSpritesFromLoaded(char: LoadedCharacterData): CharacterSprites {
  const d = char.down; // 7 frames
  const u = char.up; // 7 frames
  const rt = char.right; // 7 frames
  const flip = flipSpriteHorizontal;

  return {
    walk: {
      // Walk uses frames 0, 1, 2, 1 (walk1, walk2, walk3, walk2)
      [Dir.DOWN]: [d[0], d[1], d[2], d[1]],
      [Dir.UP]: [u[0], u[1], u[2], u[1]],
      [Dir.RIGHT]: [rt[0], rt[1], rt[2], rt[1]],
      [Dir.LEFT]: [flip(rt[0]), flip(rt[1]), flip(rt[2]), flip(rt[1])],
    },
    typing: {
      // Typing uses frames 3, 4 (type1, type2)
      [Dir.DOWN]: [d[3], d[4]],
      [Dir.UP]: [u[3], u[4]],
      [Dir.RIGHT]: [rt[3], rt[4]],
      [Dir.LEFT]: [flip(rt[3]), flip(rt[4])],
    },
    reading: {
      // Reading uses frames 5, 6 (read1, read2)
      [Dir.DOWN]: [d[5], d[6]],
      [Dir.UP]: [u[5], u[6]],
      [Dir.RIGHT]: [rt[5], rt[6]],
      [Dir.LEFT]: [flip(rt[5]), flip(rt[6])],
    },
  };
}

export function getCharacterSprites(paletteIndex: number): CharacterSprites {
  const cached = spriteCache.get(paletteIndex);
  if (cached) return cached;

  let sprites: CharacterSprites;

  // Check for loaded PNG character data first
  const loadedCharacters = getLoadedCharacters();
  if (loadedCharacters && loadedCharacters.length > 0) {
    // Use pre-colored character sprites from PNG
    const char = loadedCharacters[paletteIndex % loadedCharacters.length];
    sprites = buildSpritesFromLoaded(char);
  } else {
    // Fallback: use hardcoded templates with palette swapping
    const pal = CHARACTER_PALETTES[paletteIndex % CHARACTER_PALETTES.length];
    const r = (t: TemplateCell[][]) => resolveTemplate(t, pal);
    const rf = (t: TemplateCell[][]) => resolveTemplate(flipHorizontal(t), pal);

    sprites = {
      walk: {
        [Dir.DOWN]: [
          r(CHAR_WALK_DOWN_1),
          r(CHAR_WALK_DOWN_2),
          r(CHAR_WALK_DOWN_1),
          r(CHAR_WALK_DOWN_2),
        ],
        [Dir.UP]: [r(CHAR_WALK_UP_1), r(CHAR_WALK_UP_2), r(CHAR_WALK_UP_1), r(CHAR_WALK_UP_2)],
        [Dir.RIGHT]: [
          r(CHAR_WALK_RIGHT_1),
          r(CHAR_WALK_RIGHT_2),
          r(CHAR_WALK_RIGHT_1),
          r(CHAR_WALK_RIGHT_2),
        ],
        [Dir.LEFT]: [
          rf(CHAR_WALK_RIGHT_1),
          rf(CHAR_WALK_RIGHT_2),
          rf(CHAR_WALK_RIGHT_1),
          rf(CHAR_WALK_RIGHT_2),
        ],
      },
      typing: {
        [Dir.DOWN]: [r(CHAR_DOWN_TYPE_1), r(CHAR_DOWN_TYPE_2)],
        [Dir.UP]: [r(CHAR_UP_TYPE_1), r(CHAR_UP_TYPE_2)],
        [Dir.RIGHT]: [r(CHAR_RIGHT_TYPE_1), r(CHAR_RIGHT_TYPE_2)],
        [Dir.LEFT]: [rf(CHAR_RIGHT_TYPE_1), rf(CHAR_RIGHT_TYPE_2)],
      },
      // Fallback: use typing frames for reading (PNG has real read frames)
      reading: {
        [Dir.DOWN]: [r(CHAR_DOWN_TYPE_1), r(CHAR_DOWN_TYPE_2)],
        [Dir.UP]: [r(CHAR_UP_TYPE_1), r(CHAR_UP_TYPE_2)],
        [Dir.RIGHT]: [r(CHAR_RIGHT_TYPE_1), r(CHAR_RIGHT_TYPE_2)],
        [Dir.LEFT]: [rf(CHAR_RIGHT_TYPE_1), rf(CHAR_RIGHT_TYPE_2)],
      },
    };
  }

  spriteCache.set(paletteIndex, sprites);
  return sprites;
}

// Furniture sprites

/** Square desk: 32x32 pixels (2x2 tiles) */
export const DESK_SPRITE: SpriteData = (() => {
  const W = "#8B6914"; // wood edge
  const L = "#A07828"; // lighter wood
  const S = "#B8922E"; // surface
  const D = "#6B4E0A"; // dark edge
  const rows: string[][] = [];
  rows.push(new Array(32).fill(_));
  rows.push([_, ...new Array(30).fill(W), _]);
  for (let r = 0; r < 4; r++) {
    rows.push([_, W, ...new Array(28).fill(r < 1 ? L : S), W, _]);
  }
  rows.push([_, D, ...new Array(28).fill(W), D, _]);
  for (let r = 0; r < 6; r++) {
    rows.push([_, W, ...new Array(28).fill(S), W, _]);
  }
  rows.push([_, W, ...new Array(28).fill(L), W, _]);
  for (let r = 0; r < 6; r++) {
    rows.push([_, W, ...new Array(28).fill(S), W, _]);
  }
  rows.push([_, D, ...new Array(28).fill(W), D, _]);
  for (let r = 0; r < 4; r++) {
    rows.push([_, W, ...new Array(28).fill(r > 2 ? L : S), W, _]);
  }
  rows.push([_, ...new Array(30).fill(W), _]);
  for (let r = 0; r < 4; r++) {
    const row = new Array(32).fill(_) as string[];
    row[1] = D;
    row[2] = D;
    row[29] = D;
    row[30] = D;
    rows.push(row);
  }
  rows.push(new Array(32).fill(_));
  rows.push(new Array(32).fill(_));
  return rows;
})();

/** Chair: 16x16 */
export const CHAIR_SPRITE: SpriteData = (() => {
  const W = "#8B6914";
  const D = "#6B4E0A";
  const B = "#5C3D0A";
  const S = "#A07828";
  return [
    [_, _, _, _, _, D, D, D, D, D, D, _, _, _, _, _],
    [_, _, _, _, D, B, B, B, B, B, B, D, _, _, _, _],
    [_, _, _, _, D, B, S, S, S, S, B, D, _, _, _, _],
    [_, _, _, _, D, B, S, S, S, S, B, D, _, _, _, _],
    [_, _, _, _, D, B, S, S, S, S, B, D, _, _, _, _],
    [_, _, _, _, D, B, S, S, S, S, B, D, _, _, _, _],
    [_, _, _, _, D, B, S, S, S, S, B, D, _, _, _, _],
    [_, _, _, _, D, B, S, S, S, S, B, D, _, _, _, _],
    [_, _, _, _, D, B, S, S, S, S, B, D, _, _, _, _],
    [_, _, _, _, D, B, B, B, B, B, B, D, _, _, _, _],
    [_, _, _, _, _, D, D, D, D, D, D, _, _, _, _, _],
    [_, _, _, _, _, _, D, W, W, D, _, _, _, _, _, _],
    [_, _, _, _, _, _, D, W, W, D, _, _, _, _, _, _],
    [_, _, _, _, _, D, D, D, D, D, D, _, _, _, _, _],
    [_, _, _, _, _, D, _, _, _, _, D, _, _, _, _, _],
    [_, _, _, _, _, D, _, _, _, _, D, _, _, _, _, _],
  ];
})();

/** PC monitor: 16x16 */
export const PC_SPRITE: SpriteData = (() => {
  const F = "#555555";
  const S = "#3A3A5C";
  const B = "#6688CC";
  const D = "#444444";
  return [
    [_, _, _, F, F, F, F, F, F, F, F, F, F, _, _, _],
    [_, _, _, F, S, S, S, S, S, S, S, S, F, _, _, _],
    [_, _, _, F, S, B, B, B, B, B, B, S, F, _, _, _],
    [_, _, _, F, S, B, B, B, B, B, B, S, F, _, _, _],
    [_, _, _, F, S, B, B, B, B, B, B, S, F, _, _, _],
    [_, _, _, F, S, B, B, B, B, B, B, S, F, _, _, _],
    [_, _, _, F, S, B, B, B, B, B, B, S, F, _, _, _],
    [_, _, _, F, S, B, B, B, B, B, B, S, F, _, _, _],
    [_, _, _, F, S, S, S, S, S, S, S, S, F, _, _, _],
    [_, _, _, F, F, F, F, F, F, F, F, F, F, _, _, _],
    [_, _, _, _, _, _, _, D, D, _, _, _, _, _, _, _],
    [_, _, _, _, _, _, _, D, D, _, _, _, _, _, _, _],
    [_, _, _, _, _, _, D, D, D, D, _, _, _, _, _, _],
    [_, _, _, _, _, D, D, D, D, D, D, _, _, _, _, _],
    [_, _, _, _, _, D, D, D, D, D, D, _, _, _, _, _],
    [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  ];
})();

// Speech Bubble Sprites

/** Permission bubble: white square with "..." in amber (11x13) */
export const BUBBLE_PERMISSION_SPRITE: SpriteData = (() => {
  const B = "#555566"; // border
  const F = "#EEEEFF"; // fill
  const A = "#CCA700"; // amber dots
  return [
    [B, B, B, B, B, B, B, B, B, B, B],
    [B, F, F, F, F, F, F, F, F, F, B],
    [B, F, F, F, F, F, F, F, F, F, B],
    [B, F, F, F, F, F, F, F, F, F, B],
    [B, F, F, F, F, F, F, F, F, F, B],
    [B, F, F, A, F, A, F, A, F, F, B],
    [B, F, F, F, F, F, F, F, F, F, B],
    [B, F, F, F, F, F, F, F, F, F, B],
    [B, F, F, F, F, F, F, F, F, F, B],
    [B, B, B, B, B, B, B, B, B, B, B],
    [_, _, _, _, B, B, B, _, _, _, _],
    [_, _, _, _, _, B, _, _, _, _, _],
    [_, _, _, _, _, _, _, _, _, _, _],
  ];
})();

/** Waiting bubble: white square with green checkmark (11x13) */
export const BUBBLE_WAITING_SPRITE: SpriteData = (() => {
  const B = "#555566"; // border
  const F = "#EEEEFF"; // fill
  const G = "#44BB66"; // green check
  return [
    [_, B, B, B, B, B, B, B, B, B, _],
    [B, F, F, F, F, F, F, F, F, F, B],
    [B, F, F, F, F, F, F, F, F, F, B],
    [B, F, F, F, F, F, F, F, G, F, B],
    [B, F, F, F, F, F, F, G, F, F, B],
    [B, F, F, G, F, F, G, F, F, F, B],
    [B, F, F, F, G, G, F, F, F, F, B],
    [B, F, F, F, F, F, F, F, F, F, B],
    [B, F, F, F, F, F, F, F, F, F, B],
    [_, B, B, B, B, B, B, B, B, B, _],
    [_, _, _, _, B, B, B, _, _, _, _],
    [_, _, _, _, _, B, _, _, _, _, _],
    [_, _, _, _, _, _, _, _, _, _, _],
  ];
})();

/** Sleep bubble: ZZZ for resting personas in bunk room (11x13) */
export const BUBBLE_SLEEP_SPRITE: SpriteData = (() => {
  const B = "#555566"; // border
  const F = "#EEEEFF"; // fill
  const Z = "#6B7DB3"; // sleepy blue Z's
  return [
    [_, B, B, B, B, B, B, B, B, B, _],
    [B, F, F, F, F, F, F, F, F, F, B],
    [B, F, F, F, F, F, F, F, F, F, B],
    [B, F, Z, Z, Z, F, F, F, F, F, B],
    [B, F, F, F, Z, F, Z, Z, F, F, B],
    [B, F, F, Z, F, F, F, Z, F, F, B],
    [B, F, Z, Z, Z, F, Z, Z, F, F, B],
    [B, F, F, F, F, F, F, F, F, F, B],
    [B, F, F, F, F, F, F, F, F, F, B],
    [_, B, B, B, B, B, B, B, B, B, _],
    [_, _, _, _, B, B, B, _, _, _, _],
    [_, _, _, _, _, B, _, _, _, _, _],
    [_, _, _, _, _, _, _, _, _, _, _],
  ];
})();

/** Message bubble: chat icon for incoming messages (11x13) */
export const BUBBLE_MESSAGE_SPRITE: SpriteData = (() => {
  const B = "#FFD000"; // gold border (Rax yellow)
  const F = "#FFFFFF"; // white fill
  const M = "#1A1A2E"; // dark message icon
  return [
    [_, B, B, B, B, B, B, B, B, B, _],
    [B, F, F, F, F, F, F, F, F, F, B],
    [B, F, F, M, M, M, M, M, F, F, B],
    [B, F, M, F, F, F, F, F, M, F, B],
    [B, F, M, F, M, M, M, F, M, F, B],
    [B, F, M, F, F, F, F, F, M, F, B],
    [B, F, M, F, M, M, M, F, M, F, B],
    [B, F, F, M, M, M, M, M, F, F, B],
    [B, F, F, F, F, F, F, F, F, F, B],
    [_, B, B, B, B, B, B, B, B, B, _],
    [_, _, _, _, B, B, B, _, _, _, _],
    [_, _, _, _, _, B, _, _, _, _, _],
    [_, _, _, _, _, _, _, _, _, _, _],
  ];
})();

/** Warning bubble: red "!" for items needing attention (11x13) */
export const BUBBLE_WARNING_SPRITE: SpriteData = (() => {
  const B = "#CC3333"; // red border
  const F = "#FFEEEE"; // light red fill
  const X = "#CC0000"; // red exclamation
  return [
    [_, B, B, B, B, B, B, B, B, B, _],
    [B, F, F, F, F, F, F, F, F, F, B],
    [B, F, F, F, X, X, X, F, F, F, B],
    [B, F, F, F, X, X, X, F, F, F, B],
    [B, F, F, F, X, X, X, F, F, F, B],
    [B, F, F, F, X, X, X, F, F, F, B],
    [B, F, F, F, F, X, F, F, F, F, B],
    [B, F, F, F, X, X, X, F, F, F, B],
    [B, F, F, F, F, F, F, F, F, F, B],
    [_, B, B, B, B, B, B, B, B, B, _],
    [_, _, _, _, B, B, B, _, _, _, _],
    [_, _, _, _, _, B, _, _, _, _, _],
    [_, _, _, _, _, _, _, _, _, _, _],
  ];
})();

/** Setup bubble: gear icon for onboarding items (11x13) */
export const BUBBLE_SETUP_SPRITE: SpriteData = (() => {
  const B = "#FFD000"; // gold border (Rax yellow)
  const F = "#FFFFFF"; // white fill
  const G = "#666666"; // gear color
  return [
    [_, B, B, B, B, B, B, B, B, B, _],
    [B, F, F, F, F, G, F, F, F, F, B],
    [B, F, F, F, G, G, G, F, F, F, B],
    [B, F, F, G, G, F, G, G, F, F, B],
    [B, F, G, G, F, F, F, G, G, F, B],
    [B, F, G, G, F, F, F, G, G, F, B],
    [B, F, F, G, G, F, G, G, F, F, B],
    [B, F, F, F, G, G, G, F, F, F, B],
    [B, F, F, F, F, G, F, F, F, F, B],
    [_, B, B, B, B, B, B, B, B, B, _],
    [_, _, _, _, B, B, B, _, _, _, _],
    [_, _, _, _, _, B, _, _, _, _, _],
    [_, _, _, _, _, _, _, _, _, _, _],
  ];
})();

/** Speech bubble: user speaking to agents (11x13) - golden with speech icon */
export const BUBBLE_SPEECH_SPRITE: SpriteData = (() => {
  const B = "#FFD000"; // gold border (Rax yellow)
  const F = "#FFFFFF"; // white fill
  const T = "#1A1A2E"; // text lines
  return [
    [_, B, B, B, B, B, B, B, B, B, _],
    [B, F, F, F, F, F, F, F, F, F, B],
    [B, F, F, T, T, T, T, T, F, F, B],
    [B, F, F, F, F, F, F, F, F, F, B],
    [B, F, F, T, T, T, T, F, F, F, B],
    [B, F, F, F, F, F, F, F, F, F, B],
    [B, F, F, T, T, T, F, F, F, F, B],
    [B, F, F, F, F, F, F, F, F, F, B],
    [B, F, F, F, F, F, F, F, F, F, B],
    [_, B, B, B, B, B, B, B, B, B, _],
    [_, _, _, _, B, B, B, _, _, _, _],
    [_, _, _, _, _, B, _, _, _, _, _],
    [_, _, _, _, _, _, _, _, _, _, _],
  ];
})();

/** Discussion bubble: agent participating in discussion (11x13) - blue with chat dots */
export const BUBBLE_DISCUSS_SPRITE: SpriteData = (() => {
  const B = "#4488CC"; // blue border
  const F = "#EEFFFF"; // light blue fill
  const D = "#2266AA"; // dark blue dots
  return [
    [_, B, B, B, B, B, B, B, B, B, _],
    [B, F, F, F, F, F, F, F, F, F, B],
    [B, F, F, F, F, F, F, F, F, F, B],
    [B, F, F, F, F, F, F, F, F, F, B],
    [B, F, F, D, D, F, F, F, F, F, B],
    [B, F, F, F, F, D, D, F, F, F, B],
    [B, F, F, F, F, F, F, D, D, F, B],
    [B, F, F, F, F, F, F, F, F, F, B],
    [B, F, F, F, F, F, F, F, F, F, B],
    [_, B, B, B, B, B, B, B, B, B, _],
    [_, _, _, _, B, B, B, _, _, _, _],
    [_, _, _, _, _, B, _, _, _, _, _],
    [_, _, _, _, _, _, _, _, _, _, _],
  ];
})();

/** Success bubble: green checkmark for completed tasks (11x13) */
export const BUBBLE_SUCCESS_SPRITE: SpriteData = (() => {
  const B = "#33AA55"; // green border
  const F = "#EEFFEE"; // light green fill
  const C = "#228833"; // dark green checkmark
  return [
    [_, B, B, B, B, B, B, B, B, B, _],
    [B, F, F, F, F, F, F, F, F, F, B],
    [B, F, F, F, F, F, F, F, F, F, B],
    [B, F, F, F, F, F, F, F, C, F, B],
    [B, F, F, F, F, F, F, C, C, F, B],
    [B, F, C, F, F, F, C, C, F, F, B],
    [B, F, C, C, F, C, C, F, F, F, B],
    [B, F, F, C, C, C, F, F, F, F, B],
    [B, F, F, F, C, F, F, F, F, F, B],
    [_, B, B, B, B, B, B, B, B, B, _],
    [_, _, _, _, B, B, B, _, _, _, _],
    [_, _, _, _, _, B, _, _, _, _, _],
    [_, _, _, _, _, _, _, _, _, _, _],
  ];
})();

/** Couch: 48x24 (3 tiles wide, 1.5 tiles tall) */
export const COUCH_SPRITE: SpriteData = (() => {
  const F = "#3A3A6A"; // fabric
  const L = "#4A4A8A"; // light fabric
  const D = "#2A2A4A"; // dark/shadow
  const P = "#5555AA"; // pillow
  const rows: string[][] = [];
  // Backrest
  for (let r = 0; r < 6; r++) {
    const row = new Array(48).fill(_) as string[];
    for (let c = 2; c < 46; c++) {
      row[c] = r < 1 ? L : r < 2 ? F : F;
    }
    row[1] = D;
    row[46] = D;
    rows.push(row);
  }
  // Seat top
  rows.push([_, D, ...new Array(44).fill(L), D, _]);
  // Seat cushions
  for (let r = 0; r < 8; r++) {
    const row = new Array(48).fill(_) as string[];
    row[0] = D;
    for (let c = 1; c < 47; c++) {
      // 3 cushion segments with gaps
      if (c < 16) row[c] = c === 1 || c === 15 ? D : F;
      else if (c < 17) row[c] = D;
      else if (c < 31) row[c] = c === 16 || c === 30 ? D : F;
      else if (c < 32) row[c] = D;
      else row[c] = c === 31 || c === 46 ? D : F;
    }
    row[47] = D;
    rows.push(row);
  }
  // Bottom edge
  rows.push([_, D, ...new Array(44).fill(D), D, _]);
  // Legs
  const legRow = new Array(48).fill(_) as string[];
  legRow[4] = D;
  legRow[5] = D;
  legRow[42] = D;
  legRow[43] = D;
  rows.push([...legRow]);
  rows.push([...legRow]);
  rows.push(new Array(48).fill(_));
  rows.push(new Array(48).fill(_));
  rows.push(new Array(48).fill(_));
  rows.push(new Array(48).fill(_));
  return rows;
})();

/** Plant in pot: 16x24 */
export const PLANT_SPRITE: SpriteData = (() => {
  const G = "#44AA44"; // green leaves
  const D = "#228822"; // dark green
  const T = "#885522"; // pot terracotta
  const B = "#664411"; // pot dark
  return [
    [_, _, _, _, _, _, G, G, G, _, _, _, _, _, _, _],
    [_, _, _, _, G, G, D, G, D, G, G, _, _, _, _, _],
    [_, _, _, G, D, G, G, G, G, G, D, G, _, _, _, _],
    [_, _, G, G, G, D, G, G, G, D, G, G, G, _, _, _],
    [_, _, G, D, G, G, G, G, G, G, G, D, G, _, _, _],
    [_, G, G, G, D, G, G, G, G, G, D, G, G, G, _, _],
    [_, G, D, G, G, G, G, G, G, G, G, G, D, G, _, _],
    [_, G, G, G, G, G, D, G, D, G, G, G, G, G, _, _],
    [_, _, G, G, G, G, G, G, G, G, G, G, G, _, _, _],
    [_, _, _, G, G, G, G, G, G, G, G, G, _, _, _, _],
    [_, _, _, _, G, G, G, G, G, G, G, _, _, _, _, _],
    [_, _, _, _, _, G, G, G, G, G, _, _, _, _, _, _],
    [_, _, _, _, _, _, G, G, G, _, _, _, _, _, _, _],
    [_, _, _, _, _, _, B, B, B, _, _, _, _, _, _, _],
    [_, _, _, _, _, B, T, T, T, B, _, _, _, _, _, _],
    [_, _, _, _, B, T, T, T, T, T, B, _, _, _, _, _],
    [_, _, _, _, B, T, T, T, T, T, B, _, _, _, _, _],
    [_, _, _, _, B, T, T, T, T, T, B, _, _, _, _, _],
    [_, _, _, _, B, T, T, T, T, T, B, _, _, _, _, _],
    [_, _, _, _, _, B, T, T, T, B, _, _, _, _, _, _],
    [_, _, _, _, _, B, B, B, B, B, _, _, _, _, _, _],
    [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
    [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
    [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  ];
})();

/** Coffee machine: 16x20 */
export const COFFEE_MACHINE_SPRITE: SpriteData = (() => {
  const M = "#666666"; // metal
  const D = "#444444"; // dark metal
  const R = "#CC4444"; // red button
  const G = "#44AA44"; // green light
  const C = "#885533"; // coffee color
  return [
    [_, _, _, _, D, D, D, D, D, D, D, D, _, _, _, _],
    [_, _, _, D, M, M, M, M, M, M, M, M, D, _, _, _],
    [_, _, _, D, M, M, M, M, M, M, M, M, D, _, _, _],
    [_, _, _, D, M, M, D, D, D, D, M, M, D, _, _, _],
    [_, _, _, D, M, M, D, C, C, D, M, M, D, _, _, _],
    [_, _, _, D, M, M, D, C, C, D, M, M, D, _, _, _],
    [_, _, _, D, M, M, D, C, C, D, M, M, D, _, _, _],
    [_, _, _, D, M, M, D, D, D, D, M, M, D, _, _, _],
    [_, _, _, D, M, R, M, M, M, M, G, M, D, _, _, _],
    [_, _, _, D, M, M, M, M, M, M, M, M, D, _, _, _],
    [_, _, _, D, D, D, D, D, D, D, D, D, D, _, _, _],
    [_, _, _, D, M, M, M, M, M, M, M, M, D, _, _, _],
    [_, _, _, D, M, _, _, _, _, _, _, M, D, _, _, _],
    [_, _, _, D, M, _, _, _, _, _, _, M, D, _, _, _],
    [_, _, _, D, M, _, _, _, _, _, _, M, D, _, _, _],
    [_, _, _, D, D, D, D, D, D, D, D, D, D, _, _, _],
    [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
    [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
    [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
    [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  ];
})();

// Interactive office supply sprites - clickable objects for missing integrations

/** Office phone: 16x16 - for iMessage/communication setup */
export const PHONE_SPRITE: SpriteData = (() => {
  const B = "#333333"; // black body
  const G = "#444444"; // dark gray
  const S = "#666666"; // silver/light gray
  const L = "#88CC88"; // green light (LED)
  const D = "#222222"; // dark shadow
  return [
    [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
    [_, _, _, D, D, D, D, D, D, D, D, D, D, _, _, _],
    [_, _, D, B, B, B, B, B, B, B, B, B, B, D, _, _],
    [_, _, D, B, S, S, S, S, S, S, S, S, B, D, _, _],
    [_, _, D, B, S, G, G, G, G, G, G, S, B, D, _, _],
    [_, _, D, B, S, G, G, G, G, G, G, S, B, D, _, _],
    [_, _, D, B, S, S, S, S, S, S, S, S, B, D, _, _],
    [_, _, D, B, B, B, B, B, B, B, B, B, B, D, _, _],
    [_, _, D, B, G, G, G, _, G, G, G, _, B, D, _, _],
    [_, _, D, B, G, G, G, _, G, G, G, _, B, D, _, _],
    [_, _, D, B, G, G, G, _, G, G, G, _, B, D, _, _],
    [_, _, D, B, _, _, _, _, _, _, _, L, B, D, _, _],
    [_, _, D, B, G, G, G, _, G, G, G, _, B, D, _, _],
    [_, _, D, B, B, B, B, B, B, B, B, B, B, D, _, _],
    [_, _, _, D, D, D, D, D, D, D, D, D, D, _, _, _],
    [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  ];
})();

/** Computer monitor: 16x16 - for kernel.sh/browser setup */
export const MONITOR_SPRITE: SpriteData = (() => {
  const F = "#555555"; // frame
  const S = "#3A3A5C"; // screen bezel
  const B = "#4466AA"; // blue screen
  const D = "#333333"; // dark/stand
  const W = "#88AADD"; // window/content
  return [
    [_, _, _, F, F, F, F, F, F, F, F, F, F, _, _, _],
    [_, _, _, F, S, S, S, S, S, S, S, S, F, _, _, _],
    [_, _, _, F, S, B, B, B, B, B, B, S, F, _, _, _],
    [_, _, _, F, S, B, W, W, W, B, B, S, F, _, _, _],
    [_, _, _, F, S, B, W, B, B, B, B, S, F, _, _, _],
    [_, _, _, F, S, B, B, B, W, W, B, S, F, _, _, _],
    [_, _, _, F, S, B, B, B, W, B, B, S, F, _, _, _],
    [_, _, _, F, S, B, B, B, B, B, B, S, F, _, _, _],
    [_, _, _, F, S, S, S, S, S, S, S, S, F, _, _, _],
    [_, _, _, F, F, F, F, F, F, F, F, F, F, _, _, _],
    [_, _, _, _, _, _, _, D, D, _, _, _, _, _, _, _],
    [_, _, _, _, _, _, _, D, D, _, _, _, _, _, _, _],
    [_, _, _, _, _, _, D, D, D, D, _, _, _, _, _, _],
    [_, _, _, _, _, D, D, D, D, D, D, _, _, _, _, _],
    [_, _, _, _, _, D, D, D, D, D, D, _, _, _, _, _],
    [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  ];
})();

/** Notepad/clipboard: 16x16 - for notes integration */
export const NOTEPAD_SPRITE: SpriteData = (() => {
  const W = "#FFFFEE"; // paper white/cream
  const B = "#8B6914"; // brown clipboard
  const L = "#666688"; // lines on paper
  const C = "#AAAAAA"; // clip
  const D = "#5A4A0A"; // dark wood
  return [
    [_, _, _, _, _, C, C, C, C, C, C, _, _, _, _, _],
    [_, _, _, _, B, C, C, C, C, C, C, B, _, _, _, _],
    [_, _, _, B, B, W, W, W, W, W, W, B, B, _, _, _],
    [_, _, _, B, W, W, W, W, W, W, W, W, B, _, _, _],
    [_, _, _, B, W, L, L, L, L, L, L, W, B, _, _, _],
    [_, _, _, B, W, W, W, W, W, W, W, W, B, _, _, _],
    [_, _, _, B, W, L, L, L, L, L, W, W, B, _, _, _],
    [_, _, _, B, W, W, W, W, W, W, W, W, B, _, _, _],
    [_, _, _, B, W, L, L, L, L, L, L, W, B, _, _, _],
    [_, _, _, B, W, W, W, W, W, W, W, W, B, _, _, _],
    [_, _, _, B, W, L, L, L, L, W, W, W, B, _, _, _],
    [_, _, _, B, W, W, W, W, W, W, W, W, B, _, _, _],
    [_, _, _, B, B, W, W, W, W, W, W, B, B, _, _, _],
    [_, _, _, _, B, B, B, B, B, B, B, B, _, _, _, _],
    [_, _, _, _, _, D, D, D, D, D, D, _, _, _, _, _],
    [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  ];
})();

/** Calendar: 16x16 - for calendar integration */
export const CALENDAR_SPRITE: SpriteData = (() => {
  const R = "#CC3333"; // red header
  const W = "#FFFFFF"; // white paper
  const B = "#333333"; // black text/lines
  const G = "#888888"; // gray lines
  const D = "#AA2222"; // dark red
  return [
    [_, _, _, B, B, B, B, B, B, B, B, B, B, _, _, _],
    [_, _, _, B, D, D, R, R, R, R, D, D, B, _, _, _],
    [_, _, _, B, R, R, R, R, R, R, R, R, B, _, _, _],
    [_, _, _, B, W, W, W, W, W, W, W, W, B, _, _, _],
    [_, _, _, B, W, G, G, G, G, G, G, W, B, _, _, _],
    [_, _, _, B, W, G, B, G, B, G, B, W, B, _, _, _],
    [_, _, _, B, W, G, G, G, G, G, G, W, B, _, _, _],
    [_, _, _, B, W, G, B, G, B, G, G, W, B, _, _, _],
    [_, _, _, B, W, G, G, G, G, G, G, W, B, _, _, _],
    [_, _, _, B, W, G, G, B, G, B, G, W, B, _, _, _],
    [_, _, _, B, W, G, G, G, G, G, G, W, B, _, _, _],
    [_, _, _, B, W, W, W, W, W, W, W, W, B, _, _, _],
    [_, _, _, B, B, B, B, B, B, B, B, B, B, _, _, _],
    [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
    [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
    [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  ];
})();

/** Mail tray: 16x16 - for email integration */
export const MAIL_TRAY_SPRITE: SpriteData = (() => {
  const M = "#666666"; // metal tray
  const D = "#444444"; // dark metal
  const W = "#FFFFEE"; // white paper/envelope
  const E = "#DDDDCC"; // envelope shadow
  const B = "#333333"; // black details
  return [
    [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
    [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
    [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
    [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
    [_, _, _, _, D, D, D, D, D, D, D, D, _, _, _, _],
    [_, _, _, D, M, M, M, M, M, M, M, M, D, _, _, _],
    [_, _, D, M, W, W, W, W, W, W, W, W, M, D, _, _],
    [_, _, D, M, W, W, W, W, W, W, W, E, M, D, _, _],
    [_, _, D, M, W, W, W, B, W, W, E, E, M, D, _, _],
    [_, _, D, M, W, W, B, W, B, E, E, E, M, D, _, _],
    [_, _, D, M, W, B, W, W, W, B, E, E, M, D, _, _],
    [_, _, D, M, E, E, E, E, E, E, E, E, M, D, _, _],
    [_, _, _, D, M, M, M, M, M, M, M, M, D, _, _, _],
    [_, _, _, _, D, D, D, D, D, D, D, D, _, _, _, _],
    [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
    [_, _, _, _, _, _, _, _, _, _, _, _, _, _, _, _],
  ];
})();

/** Get the sprite for an integration type */
export function getInteractiveSupplySprite(type: string): SpriteData | null {
  switch (type) {
    case "phone":
      return PHONE_SPRITE;
    case "computer":
      return MONITOR_SPRITE;
    case "notepad":
      return NOTEPAD_SPRITE;
    case "calendar":
      return CALENDAR_SPRITE;
    case "email":
      return MAIL_TRAY_SPRITE;
    default:
      return null;
  }
}
